import { blogs, gallery, marqueeItems, services, testimonials } from "../data";
import Link from "../components/Link";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import ServiceCard from "../components/ServiceCard";

function Marquee() {
  return (
    <section className="marquee-band" aria-label="Featured service highlights">
      <div className="marquee-track">
        {[0, 1].map((group) => (
          <div className="marquee-group" aria-hidden={group === 1} key={group}>
            {marqueeItems.map((item) => (
              <span key={`${group}-${item}`}>{item}<i /></span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function StatsStrip() {
  const stats = [
    ["500+", "Happy Clients"],
    ["6", "Service Worlds"],
    ["24h", "Confirmation"],
    ["5.0", "Rating"],
  ];

  return (
    <section className="stats-band" aria-label="Salon highlights">
      {stats.map(([number, label]) => (
        <div className="stat bits-ripple" key={label}>
          <span>{number}</span>
          <em>{label}</em>
        </div>
      ))}
    </section>
  );
}

export default function Home() {
  return (
    <>
      <section className="home-hero">
        <div className="bits-aurora" aria-hidden="true" />
        <div className="bits-particles" aria-hidden="true">
          {Array.from({ length: 22 }).map((_, index) => (
            <span
              key={index}
              style={{
                "--i": index,
                left: `${(index * 47) % 100}%`,
                top: `${(index * 71) % 100}%`,
              }}
            />
          ))}
        </div>
        <div className="bits-orbit" aria-hidden="true"><span /><span /><span /></div>
        <img
          className="hero-bg-img"
          src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1700&auto=format&fit=crop&q=76"
          alt="Orchid Salon"
        />
        <div className="hero-overlay" />

        <div className="container hero-content">
          <div className="hero-left">
            <p className="hero-eyebrow">Orchid Salon & Academy / Bengaluru</p>
            <h1 className="hero-title">Where <em>Beauty</em> Blossoms.</h1>
            <p className="hero-sub">Orchid Salon & Academy - a sanctuary crafted exclusively for women</p>
            <p className="hero-copy">
              From breathtaking bridal transformations to rejuvenating spa rituals - five-star luxury services,
              a private ladies-only space, and a team that treats your beauty as art.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn-primary bits-magnetic"><span>Book an Appointment</span></Link>
              <Link to="/services" className="btn-ghost bits-magnetic">Explore Services</Link>
            </div>
            <p className="hero-hours">Ladies only / Mon-Sat 10 AM - 8 PM / Bengaluru</p>
          </div>

          <aside className="hero-right" aria-label="Salon highlights">
            <div className="hero-stats-vertical">
              <div className="hero-stat"><span className="num">500+</span><span className="lbl">Happy Clients</span></div>
              <div className="hero-stat"><span className="num">6</span><span className="lbl">Beauty Paths</span></div>
              <div className="hero-stat"><span className="num">5.0</span><span className="lbl">Client Rating</span></div>
            </div>
            <div className="hero-note bits-spotlight">
              <strong>Today at the salon</strong>
              <span>Bridal makeup, hair styling, skin treatments, spa rituals, and nail services</span>
            </div>
          </aside>
        </div>
      </section>

      <Marquee />
      <StatsStrip />

      <section className="section-pad about-preview">
        <div className="container split-grid">
          <Reveal className="stack-content">
            <p className="section-eyebrow">About Us</p>
            <h2 className="section-title">A premium beauty sanctuary in Bengaluru.</h2>
            <p className="lead">
              A premium beauty sanctuary in Bengaluru offering world-class services exclusively for women -
              where every visit is a celebration.
            </p>
            <p>
              From luminous bridal looks to editorial edge, advanced facials, precise nail art, restorative spa
              therapies, and academy-led training, Orchid brings beauty, craft, and care into one private space.
            </p>
            <ul className="check-grid">
              <li>Ladies Only</li>
              <li>Luxury Services</li>
              <li>Expert Artists</li>
              <li>Bengaluru Studio</li>
            </ul>
            <Link to="/about" className="class-link">Read about us -</Link>
          </Reveal>

          <Reveal delay={120} className="tilt-visual bits-tilt">
            <img
              src="https://images.unsplash.com/photo-1560869713-7d0a29430803?w=1000&auto=format&fit=crop&q=74"
              alt="Orchid Salon interior"
            />
            <div className="floating-note bits-spotlight">
              <strong>Guided, not rushed.</strong>
              <span>Personal attention in every appointment.</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad dark-section">
        <div className="container">
          <Reveal>
            <SectionHeader
              eyebrow="Our Services"
              title="Explore every beauty path at Orchid."
              desc="Makeup, hair, skin, spa, nails, and academy programs designed with a precise luxury salon approach."
            />
          </Reveal>
          <div className="service-grid compact">
            {services.slice(0, 3).map((service, index) => (
              <Reveal delay={index * 80} key={service.id}>
                <ServiceCard service={service} index={index} />
              </Reveal>
            ))}
          </div>
          <div className="center-action">
            <Link to="/services" className="btn-ghost bits-magnetic">View All Services</Link>
          </div>
        </div>
      </section>

      <section className="section-pad warm-section">
        <div className="container">
          <Reveal>
            <SectionHeader
              eyebrow="Gallery"
              title="Moments of craft, calm, and transformation."
            />
          </Reveal>
          <div className="image-grid">
            {gallery.slice(0, 4).map((item, index) => (
              <Reveal delay={index * 70} key={item.label}>
                <article className="image-tile bits-spotlight">
                  <img src={item.src} alt={item.label} />
                  <div><span>{String(index + 1).padStart(2, "0")}</span><h3>{item.label}</h3></div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad dark-section">
        <div className="container">
          <Reveal>
            <SectionHeader eyebrow="Stories" title="What clients feel after they visit." />
          </Reveal>
          <div className="testimonial-grid">
            {testimonials.slice(0, 3).map((item, index) => (
              <Reveal delay={index * 80} key={item.name}>
                <article className={`testi-card bits-spotlight ${index === 1 ? "featured" : ""}`}>
                  <div className="quote">"</div>
                  <p>{item.text}</p>
                  <div className="testi-author">
                    <div className="avatar">{item.avatar}</div>
                    <div><strong>{item.name}</strong><em>{item.role}</em></div>
                  </div>
                  <span className="rating">{item.rating} rating</span>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad membership-section">
        <div className="container membership-grid">
          <Reveal>
            <p className="section-eyebrow">Beauty Blog</p>
            <h2 className="section-title">Salon notes for the season.</h2>
          </Reveal>
          {blogs.map((blog, index) => (
            <Reveal delay={index * 80} key={blog.title}>
              <article className={`blog-card ${index === 1 ? "featured" : ""}`}>
                <img src={blog.image} alt={blog.title} />
                <div>
                  <span>{blog.tag} / {blog.date}</span>
                  <h3>{blog.title}</h3>
                  <p>{blog.excerpt}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-panel">
            <p className="section-eyebrow">Begin Today</p>
            <h2>Your next salon visit should feel simple to start.</h2>
            <Link to="/contact" className="btn-primary bits-magnetic"><span>Book Your Session</span></Link>
          </div>
        </div>
      </section>
    </>
  );
}
